import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Archive,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useState } from 'react';
import { ApplicationAPI, Application } from '../api/applications';

interface ArchiveApplicationProps {
  isOpen: boolean;
  onClose: () => void;
  application: Application | null;
  onSuccess?: () => void;
}

export function ArchiveApplication({ isOpen, onClose, application, onSuccess }: ArchiveApplicationProps) {
  const [isArchiving, setIsArchiving] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleArchive = async () => {
    if (!application) return;

    setIsArchiving(true);
    setError(null);

    try {
      const response = await ApplicationAPI.archive(application.id);

      if (response.code === '200') {
        setIsArchiving(false);
        setIsComplete(true);

        if (onSuccess) {
          onSuccess();
        }

        onClose();
        setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to archive application');
      }
    } catch (error) {
      setIsArchiving(false);
      setError(error instanceof Error ? error.message : 'Failed to archive application. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Application Archived Successfully!</h3>
          <p className="text-muted-foreground">
            The application "{application?.name}" has been archived and moved to the archive section.
          </p>
        </div>
      );
    }

    if (isArchiving) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Archiving Application...</h3>
          <p className="text-muted-foreground">Moving application to archive...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-muted-foreground">
              Are you sure you want to archive the application <strong>{application?.name}</strong>?
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Archive className="w-5 h-5 text-amber-600" />
            <span>Archive Application</span>
          </DialogTitle>
          <DialogDescription>
            This action will move the application to your archived applications.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>

        {!isArchiving && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchive}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Archive Application
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
